// Backoffice authentication against the backend (POST /users/login → JWT).
// Roles: admin (everything), manager (products/categories), viewer (read-only).
import { API_URL } from "./config";

const TOKEN_KEY = "lormami_admin_token";
const EMAIL_KEY = "lormami_admin_email";

export const STAFF_ROLES = ["admin", "manager", "viewer"];

function decodePayload(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function currentPayload() {
  const token = getToken();
  return token ? decodePayload(token) : null;
}

export async function login(email, password) {
  let response;
  try {
    response = await fetch(`${API_URL}/users/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
  } catch {
    throw new Error("Could not reach the server. Is the backend running?");
  }

  // The API returns plain-text messages on failure, JSON on success
  const text = await response.text();
  if (!response.ok) {
    throw new Error(text || "Login failed. Please try again.");
  }

  const data = JSON.parse(text);
  const payload = decodePayload(data.token);
  const role = payload?.role || (payload?.isAdmin ? "admin" : null);
  if (!role || !STAFF_ROLES.includes(role)) {
    throw new Error("This account does not have backoffice access.");
  }

  localStorage.setItem(TOKEN_KEY, data.token);
  localStorage.setItem(EMAIL_KEY, data.user);
  return { ...data, role };
}

export function logout() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EMAIL_KEY);
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function getAdminEmail() {
  return localStorage.getItem(EMAIL_KEY);
}

export function getRole() {
  const payload = currentPayload();
  if (!payload) return null;
  return payload.role || (payload.isAdmin ? "admin" : null);
}

export function getUserId() {
  return currentPayload()?.userId || null;
}

export function isStaffLoggedIn() {
  const payload = currentPayload();
  if (!payload) return false;
  const role = payload.role || (payload.isAdmin ? "admin" : null);
  if (!role || !STAFF_ROLES.includes(role)) return false;
  if (payload.exp && payload.exp * 1000 < Date.now()) {
    logout(); // token expired — clear it
    return false;
  }
  return true;
}

// Backward-compatible alias
export const isAdminLoggedIn = isStaffLoggedIn;

export function hasRole(...roles) {
  return isStaffLoggedIn() && roles.includes(getRole());
}

export function canManageProducts() {
  return hasRole("admin", "manager");
}

export function canManageTeam() {
  return hasRole("admin");
}

export function authHeader() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
